import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { focusNFService, FocusNFConfig } from "@/integrations/focusnf/service";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft, ExternalLink, Eye, EyeOff, Upload, FileText, QrCode, HelpCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/components/AuthProvider";
import { NotasFiscaisBlockedModal } from "@/components/NotasFiscaisBlockedModal";
import { getPlanInfo } from "@/integrations/supabase/plan-utils";

export default function ConfiguracaoFocusNF() {
  const navigate = useNavigate();
  const location = useLocation();
  const { empresa, user } = useAuth();
  const adminEmails = import.meta.env.VITE_ADMIN_EMAILS?.split(',') || [];
  const adminEmailsNormalized = new Set(
    ['brunobgs1888@gmail.com', ...adminEmails]
      .map((email) => email?.trim().toLowerCase())
      .filter(Boolean)
  );
  const isAdmin = user?.email && adminEmailsNormalized.has(user.email.toLowerCase());
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [showTokenHomologacao, setShowTokenHomologacao] = useState(false);
  const [showTokenProducao, setShowTokenProducao] = useState(false);
  const [showCertificadoSenha, setShowCertificadoSenha] = useState(false);
  // ✅ NOVO - Estados para NFC-e
  const [showNfceCsc, setShowNfceCsc] = useState(false);
  const [showNfceIdToken, setShowNfceIdToken] = useState(false);
  const [showNfseSenhaPrefeitura, setShowNfseSenhaPrefeitura] = useState(false);
  const [expandNfseSection, setExpandNfseSection] = useState(false);
  const [expandNfceSection, setExpandNfceSection] = useState(false);
  const [syncingEmitente, setSyncingEmitente] = useState(false);
  const [certificadoFile, setCertificadoFile] = useState<File | null>(null);
  const [certificadoFileName, setCertificadoFileName] = useState<string>('');
  const [config, setConfig] = useState<Partial<FocusNFConfig>>({
    ambiente: 'homologacao',
    ativo: true,
    habilita_nfe: true,
    habilita_nfce: false,
    habilita_nfse: false,
    habilita_nfsen: false,
    nfsen_homologacao_habilitada: false,
    nfsen_producao_habilitada: false,
  });
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [hasNFAccess, setHasNFAccess] = useState<boolean | null>(null);
  const accessCheckDone = useRef(false);
  const numeracaoRef = useRef<HTMLDivElement | null>(null);
  const numeracaoSerieRef = useRef<HTMLInputElement | null>(null);
  const hasEmitenteMinimo = Boolean(
    config.cnpj_emitente &&
      config.razao_social &&
      config.endereco_logradouro &&
      config.endereco_cidade &&
      config.endereco_uf &&
      config.endereco_cep
  );

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (accessCheckDone.current) return;
    if (!empresa?.id) return;

    accessCheckDone.current = true;

    getPlanInfo(empresa.id).then(planInfo => {
      setHasNFAccess(planInfo.canUseNF);
      if (!planInfo.canUseNF) {
        setShowBlockedModal(true);
      }
    }).catch(() => {
      setHasNFAccess(true);
    });
  }, []);

  useEffect(() => {
    const highlight = (location.state as { highlight?: string } | null)?.highlight;
    if (highlight === 'numeracao' && !loadingConfig && numeracaoRef.current) {
      numeracaoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      requestAnimationFrame(() => {
        numeracaoSerieRef.current?.focus();
      });
    }
  }, [loadingConfig, location.state]);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const existing = await focusNFService.getConfig();
      if (existing) {
        setConfig({
          habilita_nfe: true,
          habilita_nfce: false,
          habilita_nfse: false,
          habilita_nfsen: false,
          nfsen_homologacao_habilitada: false,
          nfsen_producao_habilitada: false,
          ...existing,
        });
        // Se já tem certificado configurado, mostrar nome do arquivo
        if (existing.certificado_arquivo) {
          setCertificadoFileName('Certificado já configurado (clique para substituir)');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleCertificadoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo (.pfx ou .p12)
    const validExtensions = ['.pfx', '.p12'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Certificado deve ser um arquivo .pfx ou .p12');
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Certificado muito grande. Máximo permitido: 5MB');
      return;
    }

    try {
      // Converter para Base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        // Remover prefixo "data:application/x-pkcs12;base64," ou similar
        const base64Data = base64.split(',')[1] || base64;
        setConfig({ ...config, certificado_arquivo: base64Data });
        setCertificadoFile(file);
        setCertificadoFileName(file.name);
      };
      reader.onerror = () => {
        toast.error('Erro ao ler arquivo do certificado');
      };
      reader.readAsDataURL(file);
    } catch (error: any) {
      console.error('Erro ao processar certificado:', error);
      toast.error('Erro ao processar certificado: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!config.cnpj_emitente || !config.razao_social) {
      toast.error('Preencha pelo menos CNPJ e Razão Social');
      return;
    }

    if (config.cnpj_emitente.replace(/\D/g, '').length !== 14) {
      toast.error('CNPJ deve ter 14 dígitos numéricos');
      return;
    }

    // Validar campos obrigatórios do endereço
    if (!config.endereco_uf || config.endereco_uf.length !== 2) {
      toast.error('Preencha o campo UF (2 letras, ex: SP, MG)');
      return;
    }

    if (!config.endereco_cidade) {
      toast.error('Preencha o campo Cidade');
      return;
    }

    if (!config.endereco_logradouro) {
      toast.error('Preencha o campo Logradouro');
      return;
    }

    if (!config.endereco_cep) {
      toast.error('Preencha o campo CEP');
      return;
    }

    if (config.endereco_cep.replace(/\D/g, '').length !== 8) {
      toast.error('CEP deve ter exatamente 8 dígitos');
      return;
    }

    if (!config.certificado_arquivo || !config.certificado_senha) {
      toast.error('Informe o certificado digital A1 e a senha');
      return;
    }

    if (config.habilita_nfse && !config.inscricao_municipal) {
      toast.error('Para habilitar NFS-e, preencha a Inscrição Municipal da empresa');
      return;
    }

    if (
      config.habilita_nfsen &&
      !config.nfsen_homologacao_habilitada &&
      !config.nfsen_producao_habilitada
    ) {
      toast.error('Para NFS-e Nacional, habilite pelo menos um ambiente (homologação ou produção)');
      return;
    }

    if (
      config.habilita_nfsen &&
      config.ambiente === 'homologacao' &&
      !config.nfsen_homologacao_habilitada
    ) {
      toast.error('Ambiente atual em homologação: habilite o ambiente de homologação da NFS-e Nacional');
      return;
    }

    if (
      config.habilita_nfsen &&
      config.ambiente === 'producao' &&
      !config.nfsen_producao_habilitada
    ) {
      toast.error('Ambiente atual em produção: habilite o ambiente de produção da NFS-e Nacional');
      return;
    }

    if (config.nfe_proximo_numero && Number(config.nfe_proximo_numero) < 1) {
      toast.error('O próximo número da NF-e deve ser maior que zero');
      return;
    }

    try {
      setLoading(true);
      const result = await focusNFService.saveConfig(config as FocusNFConfig);
      
      if (result.ok) {
        toast.success('Configuração salva com sucesso!');
        await loadConfig();
      } else {
        toast.error(result.error || 'Erro ao salvar configuração');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleAjustarNumeracao = async () => {
    if (!config.nfe_proximo_numero || Number(config.nfe_proximo_numero) < 1) {
      toast.error('Informe um próximo número válido antes de ajustar');
      return;
    }

    const confirmacao = window.confirm(
      'Ajustar a próxima numeração afeta todas as próximas emissões. Confirma a atualização?'
    );
    if (!confirmacao) return;

    try {
      setLoading(true);
      const result = await focusNFService.saveConfig(config as FocusNFConfig);
      if (result.ok) {
        toast.success('Numeração atualizada com sucesso!');
        await loadConfig();
      } else {
        toast.error(result.error || 'Erro ao atualizar numeração');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar numeração');
    } finally {
      setLoading(false);
    }
  };

  const handleCadastrarEmitente = async () => {
    try {
      setSyncingEmitente(true);
      const result = await focusNFService.cadastrarEmitente();
      if (result.ok) {
        toast.success('Emitente cadastrado na Focus NF com sucesso!');
      } else {
        toast.error(result.error || 'Erro ao cadastrar emitente');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao cadastrar emitente');
    } finally {
      setSyncingEmitente(false);
    }
  };

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return hasNFAccess === false ? (
    <NotasFiscaisBlockedModal
      open={showBlockedModal}
      onOpenChange={setShowBlockedModal}
    />
  ) : (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-10 shadow-sm">
        <div className="p-4 md:p-6">
          <div className="flex items-center gap-4">
            <SidebarTrigger className="text-gray-700 hover:bg-gray-100" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-gray-700 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Configuração de Notas Fiscais</h1>
              <p className="text-gray-600 text-sm mt-0.5">Centralize os dados fiscais da sua empresa e prepare a emissão de notas com segurança</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6 max-w-4xl">
        {/* Aviso se não tiver plano profissional */}
        {!empresa?.tem_nota_fiscal && empresa?.is_premium && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardHeader>
              <CardTitle className="text-yellow-900">⚠️ Plano Profissional Necessário</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-yellow-800">
                Você está usando o <strong>Plano Básico</strong>. Para emitir notas fiscais, você precisa do <strong>Plano Profissional</strong>.
              </p>
              <Button 
                onClick={() => navigate('/assinatura')}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Ver Planos Disponíveis
              </Button>
              <p className="text-sm text-yellow-700">
                Você pode configurar os dados aqui, mas não poderá emitir notas até fazer upgrade para o Plano Profissional.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Resumo simples de uso de notas fiscais (link para gestão) */}
        {empresa?.tem_nota_fiscal && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-700" />
                Acompanhamento de notas fiscais
              </CardTitle>
              <CardDescription className="text-xs text-blue-700">
                Veja o uso mensal de notas fiscais e detalhes das emissões na tela de Gestão de Notas Fiscais.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-blue-800">
                Use esta tela apenas para configurar a integração. Para acompanhar volume de notas e limites do plano,
                acesse a Gestão de Notas.
              </p>
              <Button
                variant="outline"
                className="border-blue-300 text-blue-800 hover:bg-blue-100"
                onClick={() => navigate("/gestao-notas-fiscais")}
              >
                <FileText className="w-4 h-4 mr-2" />
                Abrir Gestão de Notas
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Dados Fiscais e Emissão</CardTitle>
            <CardDescription>
              Preencha e revise os dados da sua empresa para habilitar a emissão de NFe, NFC-e e NFS-e com consistência e conformidade.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ambiente e Tokens */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Ambiente e Autenticação</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ambiente">Ambiente</Label>
                    <Select
                      value={config.ambiente}
                      onValueChange={(value: 'homologacao' | 'producao') => 
                        setConfig({ ...config, ambiente: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="homologacao">Homologação (Testes)</SelectItem>
                        <SelectItem value="producao">Produção</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Use homologação durante os 30 dias de teste
                    </p>
                  </div>
                </div>

                {config.ambiente === 'homologacao' && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 space-y-2">
                    <p className="font-semibold">Homologação: siga os passos abaixo</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-800">
                      <li>Passo 1: Cadastre o emitente na Focus (use o botão abaixo).</li>
                      <li>Passo 2: Solicite/libere o Token de Homologação e cole aqui.</li>
                      <li>Passo 3: Salve e depois emita em homologação.</li>
                    </ol>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Para emitir em homologação é obrigatório cadastrar o emitente primeiro e informar o token de homologação.
                  O token de produção continua sendo gerenciado pela equipe Ateliê Pro na conta guarda‑chuva.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="token_homologacao">Token Homologação</Label>
                    <div className="relative">
                      <Input
                        id="token_homologacao"
                        type={showTokenHomologacao ? "text" : "password"}
                        value={config.token_homologacao || ''}
                        onChange={(e) => setConfig({ ...config, token_homologacao: e.target.value })}
                        placeholder="Token de homologação"
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowTokenHomologacao(!showTokenHomologacao)}
                      >
                        {showTokenHomologacao ? (
                          <EyeOff className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-500" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use este token apenas para emissões em homologação.
                    </p>
                  </div>

                  {isAdmin ? (
                    <div className="space-y-2">
                      <Label htmlFor="token_producao">Token Produção</Label>
                      <div className="relative">
                        <Input
                          id="token_producao"
                          type={showTokenProducao ? "text" : "password"}
                          value={config.token_producao || ''}
                          onChange={(e) => setConfig({ ...config, token_producao: e.target.value })}
                          placeholder="Token de produção"
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowTokenProducao(!showTokenProducao)}
                        >
                          {showTokenProducao ? (
                            <EyeOff className="h-4 w-4 text-gray-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20 p-4 text-xs text-muted-foreground">
                      Token de produção protegido. Esta seção é visível apenas para administradores.
                    </div>
                  )}
                </div>
              </div>

              {/* Documentos Fiscais (toggles) */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold">Documentos Fiscais</h3>
                <p className="text-sm text-muted-foreground">
                  Ative apenas os tipos de nota que você realmente vai usar.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <label className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={config.habilita_nfe ?? true}
                      onChange={(e) => setConfig({ ...config, habilita_nfe: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">NFe (Modelo 55)</p>
                      <p className="text-xs text-slate-600">Notas de produto para vendas em geral.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={config.habilita_nfce ?? false}
                      onChange={(e) => setConfig({ ...config, habilita_nfce: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">NFC-e (Modelo 65)</p>
                      <p className="text-xs text-slate-600">Vendas de balcão/PDV para consumidor final.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={config.habilita_nfse ?? false}
                      onChange={(e) => setConfig({ ...config, habilita_nfse: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">NFS-e</p>
                      <p className="text-xs text-slate-600">Emissão de notas fiscais de serviços.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={config.habilita_nfsen ?? false}
                      onChange={(e) => setConfig({ ...config, habilita_nfsen: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">NFS-e Nacional (NFSEN)</p>
                      <p className="text-xs text-slate-600">Recomendado para MEI quando a Focus exigir ambiente nacional.</p>
                    </div>
                  </label>
                </div>

                {(config.habilita_nfsen ?? false) && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                    <p className="text-sm font-semibold text-amber-900">Ambientes NFS-e Nacional</p>
                    <p className="text-xs text-amber-800">
                      Marque os ambientes que já estão habilitados no painel da Focus para evitar falhas na emissão.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center gap-3 rounded-md border border-amber-200 bg-white px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.nfsen_homologacao_habilitada ?? false}
                          onChange={(e) =>
                            setConfig({ ...config, nfsen_homologacao_habilitada: e.target.checked })
                          }
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-slate-800">Homologação habilitada na Focus</span>
                      </label>

                      <label className="flex items-center gap-3 rounded-md border border-amber-200 bg-white px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.nfsen_producao_habilitada ?? false}
                          onChange={(e) =>
                            setConfig({ ...config, nfsen_producao_habilitada: e.target.checked })
                          }
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-slate-800">Produção habilitada na Focus</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Dados da Empresa */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Dados da Empresa Emitente</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cnpj">CNPJ *</Label>
                    <Input
                      id="cnpj"
                      value={config.cnpj_emitente || ''}
                      onChange={(e) => {
                        // Aceitar apenas números
                        let valor = e.target.value.replace(/\D/g, '');
                        // Limitar a 14 dígitos (CNPJ)
                        valor = valor.substring(0, 14);
                        setConfig({ ...config, cnpj_emitente: valor });
                      }}
                      placeholder="00000000000000"
                      maxLength={14}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Apenas números (14 dígitos)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="razao_social">Razão Social *</Label>
                    <Input
                      id="razao_social"
                      value={config.razao_social || ''}
                      onChange={(e) => setConfig({ ...config, razao_social: e.target.value })}
                      placeholder="Razão Social"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nome_fantasia">Nome Fantasia</Label>
                    <Input
                      id="nome_fantasia"
                      value={config.nome_fantasia || ''}
                      onChange={(e) => setConfig({ ...config, nome_fantasia: e.target.value })}
                      placeholder="Nome Fantasia"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
                    <Input
                      id="inscricao_estadual"
                      value={config.inscricao_estadual || ''}
                      onChange={(e) => setConfig({ ...config, inscricao_estadual: e.target.value })}
                      placeholder="Inscrição Estadual"
                    />
                  </div>

                      <div className="space-y-2">
                        <Label htmlFor="inscricao_municipal">Inscrição Municipal</Label>
                        <Input
                          id="inscricao_municipal"
                          value={config.inscricao_municipal || ''}
                          onChange={(e) => setConfig({ ...config, inscricao_municipal: e.target.value })}
                          placeholder="Inscrição Municipal"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="regime_tributario">Regime Tributário *</Label>
                        <Select
                          value={config.regime_tributario || 'mei'}
                          onValueChange={(value: 'mei' | 'simples_nacional' | 'simples_excesso_sublimite' | 'regime_normal') => 
                            setConfig({ ...config, regime_tributario: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="simples_nacional">Simples Nacional</SelectItem>
                            <SelectItem value="mei">Simples Nacional - Microempreendedor Individual (MEI)</SelectItem>
                            <SelectItem value="simples_excesso_sublimite">Simples Nacional - Excesso de Sublimite</SelectItem>
                            <SelectItem value="regime_normal">Regime Normal (Lucro Presumido/Real)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          ⚠️ Importante: Selecione o regime correto conforme registrado na Receita Federal
                        </p>
                      </div>
                    </div>
                  </div>

              {/* Endereço */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Endereço</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="logradouro">Logradouro</Label>
                    <Input
                      id="logradouro"
                      value={config.endereco_logradouro || ''}
                      onChange={(e) => setConfig({ ...config, endereco_logradouro: e.target.value })}
                      placeholder="Rua, Avenida, etc."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      value={config.endereco_numero || ''}
                      onChange={(e) => setConfig({ ...config, endereco_numero: e.target.value })}
                      placeholder="Número"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={config.endereco_complemento || ''}
                      onChange={(e) => setConfig({ ...config, endereco_complemento: e.target.value })}
                      placeholder="Apt, Sala, etc."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={config.endereco_bairro || ''}
                      onChange={(e) => setConfig({ ...config, endereco_bairro: e.target.value })}
                      placeholder="Bairro"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={config.endereco_cidade || ''}
                      onChange={(e) => setConfig({ ...config, endereco_cidade: e.target.value })}
                      placeholder="Cidade"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="uf">UF *</Label>
                    <Input
                      id="uf"
                      value={config.endereco_uf || ''}
                      onChange={(e) => setConfig({ ...config, endereco_uf: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 2) })}
                      placeholder="SP"
                      maxLength={2}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Obrigatório. Exemplo: SP, MG, RJ
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP *</Label>
                    <Input
                      id="cep"
                      value={config.endereco_cep || ''}
                      onChange={(e) => {
                        // Aceitar apenas números e formatação
                        let valor = e.target.value.replace(/\D/g, '');
                        // Limitar a 8 dígitos
                        valor = valor.substring(0, 8);
                        setConfig({ ...config, endereco_cep: valor });
                      }}
                      placeholder="00000000"
                      maxLength={8}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Obrigatório. Apenas números (8 dígitos)
                    </p>
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={config.telefone || ''}
                    onChange={(e) => {
                      // Aceitar apenas números
                      let valor = e.target.value.replace(/\D/g, '');
                      // Se começar com 55 (código do país), remover
                      if (valor.startsWith('55') && valor.length > 11) {
                        valor = valor.substring(2);
                      }
                      // Limitar a 11 dígitos
                      valor = valor.substring(0, 11);
                      setConfig({ ...config, telefone: valor });
                    }}
                    placeholder="35996911688"
                    maxLength={11}
                  />
                  <p className="text-xs text-muted-foreground">
                    Apenas números (sem DDD + código do país)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={config.email || ''}
                    onChange={(e) => setConfig({ ...config, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>

              {/* Numeração NF-e */}
              <div className="space-y-4 border-t pt-6" ref={numeracaoRef}>
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold">Numeração NF-e</h3>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-emerald-800">
                    Controle a série e o próximo número para evitar rejeições de duplicidade (ex.: rejeição 539).
                  </p>
                  <p className="text-xs text-emerald-700">
                    Ajuste com orientação da contabilidade/SEFAZ antes de reemitir.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nfe_serie">Série</Label>
                    <Input
                      id="nfe_serie"
                      ref={numeracaoSerieRef}
                      value={config.nfe_serie || ''}
                      onChange={(e) =>
                        setConfig({ ...config, nfe_serie: e.target.value })
                      }
                      placeholder="1"
                      maxLength={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Ex.: 1 ou 001 (a Focus aceita texto curto).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nfe_proximo_numero">Próximo Número</Label>
                    <Input
                      id="nfe_proximo_numero"
                      inputMode="numeric"
                      value={config.nfe_proximo_numero ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setConfig({
                          ...config,
                          nfe_proximo_numero: value ? Number(value) : undefined,
                        });
                      }}
                      placeholder="1"
                    />
                    <p className="text-xs text-muted-foreground">
                      Apenas números. Será usado na próxima emissão de NF-e.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAjustarNumeracao}
                    disabled={loading}
                  >
                    Ajustar próxima numeração
                  </Button>
                  <p className="text-xs text-muted-foreground self-center">
                    Este ajuste salva imediatamente a configuração de numeração.
                  </p>
                </div>
              </div>

              {/* Certificado Digital A1 (Obrigatório) */}
              <div className="space-y-4 border-t pt-6">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold">Certificado Digital A1 (Obrigatório)</h3>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Informação:</strong> É necessário um certificado digital A1 para emitir notas fiscais.
                    Envie o arquivo (.pfx/.p12) e a senha correspondente nos campos abaixo.
                  </p>
                  <p className="text-sm text-blue-800">
                    Não possui certificado digital?{' '}
                    <a
                      href="https://wa.me/5561982321534?text=Olá! Preciso adquirir um certificado digital A1 para o Ateliê PRO."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-600 hover:text-blue-700 underline"
                    >
                      Entre em contato com nossa equipe
                    </a>
                    {' '}para adquirir seu certificado A1.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="certificado">Arquivo do Certificado (.pfx ou .p12) *</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="certificado"
                        type="file"
                        accept=".pfx,.p12"
                        onChange={handleCertificadoFileChange}
                        className="hidden"
                        required={!config.certificado_arquivo}
                      />
                      <Label
                        htmlFor="certificado"
                        className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">
                          {certificadoFileName || 'Selecionar arquivo'}
                        </span>
                      </Label>
                      {certificadoFileName && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCertificadoFile(null);
                            setCertificadoFileName('');
                            setConfig({ 
                              ...config, 
                              certificado_arquivo: undefined,
                              certificado_senha: undefined 
                            });
                          }}
                        >
                          Limpar
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Máximo: 5MB. Formatos aceitos: .pfx, .p12
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="certificado_senha">Senha do Certificado *</Label>
                    <div className="relative">
                      <Input
                        id="certificado_senha"
                        type={showCertificadoSenha ? "text" : "password"}
                        value={config.certificado_senha || ''}
                        onChange={(e) => setConfig({ ...config, certificado_senha: e.target.value })}
                        placeholder="Senha do certificado (se houver)"
                        className="pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowCertificadoSenha(!showCertificadoSenha)}
                      >
                        {showCertificadoSenha ? (
                          <EyeOff className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-500" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Senha do certificado digital (se necessário)
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Segurança:</strong> O certificado e senha serão armazenados de forma segura no banco de dados. 
                    Certifique-se de que você tem permissão para usar este certificado digital.
                  </p>
                </div>
              </div>

                {/* ✅ Configuração NFS-e (recolhível) */}
                {(config.habilita_nfse ?? false) && (
                  <div className="border rounded-lg overflow-hidden border-emerald-200">
                    <button
                      type="button"
                      onClick={() => setExpandNfseSection(!expandNfseSection)}
                      className="w-full flex items-center justify-between gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-all duration-200 border-b border-emerald-200"
                    >
                      <div className="flex items-center gap-3 text-left">
                        <FileText className="w-5 h-5 text-emerald-600" />
                        <h3 className="text-lg font-semibold text-emerald-900">Configuração NFS-e</h3>
                      </div>
                      <div className={`transform transition-transform duration-300 ${expandNfseSection ? 'rotate-180' : ''}`}>
                        <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </button>

                    {expandNfseSection && (
                      <div className="p-6 space-y-4 border-t border-emerald-200">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                          <p className="text-sm text-emerald-900 font-medium">
                            Preencha os dados de NFS-e conforme orientação da sua prefeitura/contador.
                          </p>
                          <ul className="list-disc list-inside text-xs text-emerald-800 space-y-1">
                            <li>Inscrição Municipal da empresa é obrigatória para NFS-e.</li>
                            <li>Use numeração de RPS separada para homologação e produção.</li>
                            <li>Login/senha da prefeitura só é necessário quando seu município exigir.</li>
                          </ul>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="nfse_serie_homologacao">Série NFS-e (Homologação)</Label>
                            <Input
                              id="nfse_serie_homologacao"
                              value={config.nfse_serie_homologacao || ''}
                              onChange={(e) => setConfig({ ...config, nfse_serie_homologacao: e.target.value })}
                              placeholder="1"
                              maxLength={6}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="nfse_proximo_rps_homologacao">Próximo RPS (Homologação)</Label>
                            <Input
                              id="nfse_proximo_rps_homologacao"
                              inputMode="numeric"
                              value={config.nfse_proximo_rps_homologacao ?? ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setConfig({
                                  ...config,
                                  nfse_proximo_rps_homologacao: value ? Number(value) : undefined,
                                });
                              }}
                              placeholder="1"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="nfse_serie_producao">Série NFS-e (Produção)</Label>
                            <Input
                              id="nfse_serie_producao"
                              value={config.nfse_serie_producao || ''}
                              onChange={(e) => setConfig({ ...config, nfse_serie_producao: e.target.value })}
                              placeholder="1"
                              maxLength={6}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="nfse_proximo_rps_producao">Próximo RPS (Produção)</Label>
                            <Input
                              id="nfse_proximo_rps_producao"
                              inputMode="numeric"
                              value={config.nfse_proximo_rps_producao ?? ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setConfig({
                                  ...config,
                                  nfse_proximo_rps_producao: value ? Number(value) : undefined,
                                });
                              }}
                              placeholder="1"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="nfse_login_prefeitura">Login da Prefeitura (opcional)</Label>
                            <Input
                              id="nfse_login_prefeitura"
                              value={config.nfse_login_prefeitura || ''}
                              onChange={(e) => setConfig({ ...config, nfse_login_prefeitura: e.target.value })}
                              placeholder="Usuário de acesso municipal"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="nfse_senha_prefeitura">Senha da Prefeitura (opcional)</Label>
                            <div className="relative">
                              <Input
                                id="nfse_senha_prefeitura"
                                type={showNfseSenhaPrefeitura ? 'text' : 'password'}
                                value={config.nfse_senha_prefeitura || ''}
                                onChange={(e) => setConfig({ ...config, nfse_senha_prefeitura: e.target.value })}
                                placeholder="Senha de acesso municipal"
                                className="pr-10"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowNfseSenhaPrefeitura(!showNfseSenhaPrefeitura)}
                              >
                                {showNfseSenhaPrefeitura ? (
                                  <EyeOff className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <Eye className="h-4 w-4 text-gray-500" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* ✅ NOVO - Configuração NFC-e (Modelo 65) - MOVIDA PARA APÓS NFS-e */}
              {/* Veja seção recolhível abaixo */}

            <div className="mt-6 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-900">Cadastro automático do emitente</p>
                  <p className="text-xs text-slate-600">
                    Clique para registrar/atualizar o CNPJ no painel da Focus usando a integração do Ateliê Pro.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleCadastrarEmitente}
                  disabled={syncingEmitente || !hasEmitenteMinimo}
                  className="sm:w-auto"
                >
                  {syncingEmitente ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Cadastrar emitente na Focus
                    </>
                  )}
                </Button>
              </div>
              <p className="mt-3 text-xs text-slate-600">
                Dica: salve os dados acima antes de sincronizar. Se houver erro, verifique CNPJ, endereço e regime tributário.
                {config.ambiente === 'homologacao' && (
                  <> Em homologação, o token pode ser preenchido depois do cadastro.</>
                )}
              </p>
              {!hasEmitenteMinimo && (
                <p className="mt-2 text-xs text-slate-500">
                  Preencha CNPJ, razão social, logradouro, cidade, UF e CEP para habilitar o cadastro do emitente.
                </p>
              )}
            </div>

            {/* ✅ NOVO - Seção NFC-e Recolhível (Modelo 65) */}
            {(config.habilita_nfce ?? false) && (
              <div className="border rounded-lg overflow-hidden">
                {/* Cabeçalho Recolhível */}
                <button
                  type="button"
                  onClick={() => setExpandNfceSection(!expandNfceSection)}
                  className="w-full flex items-center justify-between gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 transition-all duration-200 border-b border-blue-200"
                >
                  <div className="flex items-center gap-3 text-left">
                    <QrCode className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-blue-900">Configuração NFC-e (Modelo 65)</h3>
                  </div>
                  <div className={`transform transition-transform duration-300 ${expandNfceSection ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                </button>

                {/* Conteúdo Recolhível */}
                {expandNfceSection && (
                  <div className="p-6 space-y-6 border-t border-blue-200">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-blue-900 font-semibold">
                        📍 Como obter CSC e ID Token?
                      </p>
                      <ol className="text-xs text-blue-800 space-y-2 list-decimal list-inside">
                        <li>
                          <strong>Acesse o portal SIARE da sua UF:</strong>
                          <div className="ml-5 mt-1 text-blue-700">
                            <small>
                              • <strong>MG:</strong> https://siare.sefaz.mg.gov.br<br/>
                              • <strong>SP:</strong> https://siare.sefaz.sp.gov.br<br/>
                              • <strong>BA:</strong> https://siare.sefaz.ba.gov.br<br/>
                              • <strong>RJ:</strong> https://siare.sefaz.rj.gov.br<br/>
                              • <strong>Outras UFs:</strong> https://siare.sefaz.[uf].gov.br (substitua [uf] pela sigla)
                            </small>
                          </div>
                        </li>
                        <li>Faça login com sua IE (Inscrição Estadual) ou CNPJ</li>
                        <li>Localize a opção <strong>DOCUMENTOS ELETRÔNICOS → MANUTENÇÃO CADASTRO EMISSOR</strong> (ou semelhante na sua UF)</li>
                        <li>Procure pelos campos <strong>CSC</strong> (Código de Segurança) e <strong>ID Token</strong></li>
                        <li>Copie os valores com cuidado (respeitando maiúsculas/minúsculas) e cole nos campos abaixo</li>
                      </ol>
                      <p className="text-xs text-blue-700 mt-3 bg-blue-100 p-2 rounded">
                        ℹ️ <strong>Importante:</strong> CSC e ID Token são os mesmos para homologação e produção. Se não conseguir, entre em contato com a Sefaz da sua UF.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* CSC */}
                      <div className="space-y-2">
                        <Label htmlFor="nfce_csc">CSC da NFC-e (Código de Segurança)</Label>
                        <div className="relative">
                          <Input
                            id="nfce_csc"
                            type={showNfceCsc ? "text" : "password"}
                            value={config.nfce_csc || ''}
                            onChange={(e) => setConfig({ ...config, nfce_csc: e.target.value })}
                            placeholder="Exemplo: 1a2b3c4d5e6f (8 caracteres)"
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowNfceCsc(!showNfceCsc)}
                          >
                            {showNfceCsc ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Código de 8 caracteres fornecido pela SEFAZ da sua UF. Verifique se está correto (case-sensitive).
                        </p>
                      </div>

                      {/* ID Token */}
                      <div className="space-y-2">
                        <Label htmlFor="nfce_id_token">ID Token da NFC-e</Label>
                        <div className="relative">
                          <Input
                            id="nfce_id_token"
                            type={showNfceIdToken ? "text" : "password"}
                            value={config.nfce_id_token || ''}
                            onChange={(e) => setConfig({ ...config, nfce_id_token: e.target.value })}
                            placeholder="Encontrado no mesmo local do CSC no SIARE"
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowNfceIdToken(!showNfceIdToken)}
                          >
                            {showNfceIdToken ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Encontrado no mesmo local do CSC na sua Sefaz. Geralmente tem vários dígitos de números ou caracteres.
                        </p>
                      </div>
                    </div>

                    {/* Numeração NFC-e (opcional) */}
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-3">Numeração NFC-e (Opcional)</p>
                      <p className="text-xs text-muted-foreground mb-3">Se deixar em branco, será usada a numeração padrão de NF-e. Configure apenas se sua UF exigir numeração separada.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="nfce_serie">Série NFC-e</Label>
                          <Input
                            id="nfce_serie"
                            value={config.nfce_serie || ''}
                            onChange={(e) => setConfig({ ...config, nfce_serie: e.target.value })}
                            placeholder="1"
                            maxLength={3}
                          />
                          <p className="text-xs text-muted-foreground">
                            Se deixar em branco, usará a série de NF-e
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nfce_proximo_numero">Próximo Número NFC-e</Label>
                          <Input
                            id="nfce_proximo_numero"
                            inputMode="numeric"
                            value={config.nfce_proximo_numero ?? ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              setConfig({
                                ...config,
                                nfce_proximo_numero: value ? Number(value) : undefined,
                              });
                            }}
                            placeholder="1"
                          />
                          <p className="text-xs text-muted-foreground">
                            Se deixar em branco, usará o número de NF-e
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Toggle para habilitar/desabilitar */}
                    <div className="flex items-center gap-3 border-t pt-4">
                      <input
                        type="checkbox"
                        id="nfce_habilitada"
                        checked={config.nfce_habilitada ?? false}
                        onChange={(e) => setConfig({ ...config, nfce_habilitada: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <Label htmlFor="nfce_habilitada" className="font-medium cursor-pointer">
                        ✅ NFC-e Habilitada no PDV
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="pt-2">
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Configuração
                  </>
                )}
              </Button>
            </div>
          </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>📋 Passo a Passo para Configurar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-blue-900">Como configurar de forma rápida</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                <li>
                  <strong>Preencha os dados da empresa:</strong> informe CNPJ, razão social, endereço e regime tributário.
                </li>
                <li>
                  <strong>Configure os documentos fiscais:</strong> ative NFe, NFC-e e/ou NFS-e conforme sua operação.
                </li>
                <li>
                  <strong>Revise certificado e numeração:</strong> valide certificado A1, séries e próximo número/RPS.
                </li>
                <li>
                  <strong>Salve e teste:</strong> após salvar, faça uma emissão de teste no ambiente adequado.
                </li>
              </ol>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-yellow-900">⚠️ Importante</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                <li>Você precisa ter o <strong>plano Profissional (com NF)</strong> para usar esta funcionalidade</li>
                <li>Mantenha CNPJ, endereço e regime tributário sempre atualizados</li>
                <li>Use homologação para validar o fluxo antes de emitir em produção</li>
                <li>Para NFC-e e NFS-e, confirme os campos específicos exigidos pela sua UF/município</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-red-900">🔍 Checklist final antes de emitir</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-red-800">
                <li>
                  <strong>Cadastro da empresa completo:</strong> CNPJ, razão social, endereço, UF e CEP preenchidos corretamente.
                </li>
                <li>
                  <strong>Certificado digital válido:</strong> arquivo A1 e senha informados.
                </li>
                <li>
                  <strong>Numeração revisada:</strong> série e próximo número (ou RPS) conferidos.
                </li>
                <li>
                  <strong>Ambiente correto:</strong> homologação para testes e produção para emissão oficial.
                </li>
                <li>
                  <strong>Campos por tipo de nota:</strong> revise as exigências de NFe, NFC-e e NFS-e conforme uso.
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li>IDENTIFICAÇÃO: Razão Social, Nome Fantasia, Inscrição Estadual, Inscrição Municipal</li>
                    <li>CONTATO: Email, telefone</li>
                    <li>ENDEREÇO: Logradouro, número, bairro, cidade, UF, CEP</li>
                    <li>REGIME: Regime tributário correto</li>
                  </ul>
                </li>
                <li>
                  <strong>Suporte:</strong> em caso de erro persistente, acione o suporte com a mensagem exibida no sistema.
                </li>
              </ol>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium">📚 Documentação:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <a href="https://focusnfe.com.br/doc/" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-1">
                    Documentação completa da API <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li>
                  <a href="https://focusnfe.com.br/doc/#empresas" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-1">
                    Endpoint de empresas (emitentes) <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-200 bg-indigo-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-indigo-900 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-indigo-700" />
              Precisa de ajuda para configurar?
            </CardTitle>
            <CardDescription className="text-indigo-800">
              Acesse o guia completo com orientações para NFe, NFC-e e NFS-e, além de dúvidas frequentes.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-indigo-900">
              Você encontra passo a passo, campos obrigatórios e instruções por UF para finalizar sua configuração sem dor de cabeça.
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-indigo-300 text-indigo-800 hover:bg-indigo-100"
              onClick={() => navigate('/ajuda-notas-fiscais')}
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Abrir página de ajuda
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
